<script>
import { mapActions, mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import CreateStaffServicesModal from './createStaffServicesModal.vue';

export default {
  components: {
    CreateStaffServicesModal,
  },
  props: {
    staffId: {
      type: String,
      default: null,
    },
    branchId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      staffServices: {
        results: [],
        page: 1,
        total: 0,
        next: -1,
      },
      isAssignStaffService: false,
      currentPage: 1,
      pageSize: 10,
      isLoading: false,
    };
  },
  computed: {
    paginatedServices() {
      return this.staffServices.results || [];
    },
    totalPages() {
      if (!this.staffServices.total) return 0;
      return Math.ceil(this.staffServices.total / this.pageSize);
    },
  },
  methods: {
    ...mapActions('appointments', [
      'fetchStaffServices', 
      'deleteStaffService',
    ]),
    resetAssignStaffService() {
      this.isAssignStaffService = false;
    },
    async refreshPage() {
      try {
        this.isLoading = true;
        this.staffServices = await this.fetchStaffServices({
          staffId: this.staffId,
          page: this.currentPage,
          page_size: this.pageSize,
        });
      } catch (err) {
        console.error(err);
        useAlert(err); // TODO: i18n this if error is a string key
      } finally {
        this.isLoading = false;
      }
    },
    async handleDeleteStaffService(payload) {
      try {
        await this.deleteStaffService(payload);
      } catch (err) {
        console.error(err);
        useAlert(err); // TODO: i18n this if error is a string key
      } finally {
        this.refreshPage();
      }
    },
    handlePageSizeChange() {
      this.currentPage = 1;
      this.refreshPage();
    },
    handleNextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.refreshPage();
      }
    },
    handlePreviousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.refreshPage();
      }
    },
  },
  async mounted() {
    await this.refreshPage();
  }
}
</script>

<template>
  <div class="p-4 bg-white dark:bg-slate-800 rounded-lg shadow-md">
    <!-- Modals -->
    <div 
      v-if="isAssignStaffService" 
      class="fixed inset-0 flex items-center justify-center h-[100vh] w-[100vw] bg-slate-900/50 dark:bg-slate-800/50 z-50"
    >
      <CreateStaffServicesModal
        :staffId="staffId" 
        :branchId="branchId" 
        :resetAssignStaffService="resetAssignStaffService"
        :refreshPage="refreshPage"
      />
    </div>

    <!-- Header -->
    <div class="flex justify-between items-center mb-4 flex-wrap gap-2">
      <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
        {{ $t('BRANCHES.VIEW_STAFF_SERVICES.TITLE') }}
      </h2>
      <woot-button
        variant="smooth"
        color-scheme="primary"
        icon="plus-sign"
        size="small"
        @click="isAssignStaffService = true"
      >
        {{ $t('BRANCHES.VIEW_STAFF_SERVICES.ASSIGN_SERVICES') }}
      </woot-button>
    </div>

    <!-- Pagination Controls -->
    <div v-if="staffId && staffServices.results && staffServices.results.length > 0" class="flex justify-between items-center mb-4 gap-4">
      <!-- Page size control -->
      <select v-model.number="pageSize" @change="handlePageSizeChange" class="w-36">
        <option :value="5">5 per page</option>
        <option :value="10">10 per page</option>
        <option :value="15">15 per page</option>
        <option :value="20">20 per page</option>
      </select>

      <!-- Page control -->
      <div class="flex gap-4 items-center">
        <woot-button color-scheme="secondary" @click="handlePreviousPage" :disabled="currentPage === 1 || isLoading">
          <fluent-icon icon="chevron-left" />
          Prev
        </woot-button>
        <span class="text-sm font-medium text-slate-700 dark:text-slate-300">{{ currentPage }} / {{ totalPages }}</span>
        <woot-button color-scheme="secondary" @click="handleNextPage" :disabled="currentPage === totalPages || isLoading">
          Next
          <fluent-icon icon="chevron-right" />
        </woot-button>
      </div>
    </div>

    <!-- Service List -->
    <div v-if="staffId && staffServices.results.length !== 0">
      <div 
        v-for="staffService in paginatedServices" 
        :key="staffService.service_id"
        class="flex items-center p-3 border-b last:border-none"
      >
        <!-- Service Image/Icon -->
        <div class="mr-3 w-12 h-12 rounded-md overflow-hidden flex-shrink-0">
          <img 
            v-if="staffService.presigned_image_url" 
            :src="staffService.presigned_image_url" 
            class="w-full h-full object-cover"
            :alt="$t('BRANCHES.VIEW_STAFF_SERVICES.SERVICE_IMAGE_ALT')"
          />
          <div v-else class="w-full h-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
            <fluent-icon icon="calendar" size="20" class="text-slate-500 dark:text-slate-400" />
          </div>
        </div>

        <!-- Service Info -->
        <div class="flex-grow">
          <div class="flex items-center gap-2">
            <p class="text-md font-medium text-slate-800 dark:text-slate-100 m-0">
              {{ staffService.service_name }}
            </p>
          </div>
        </div>

        <!-- Delete Button -->
        <button 
          @click="handleDeleteStaffService({ staffId: staffId, serviceId: staffService.service_id })"
          class="ml-4 px-2 py-1 text-xs font-medium text-red-600 border border-red-600 rounded hover:bg-red-600 hover:text-white transition"
        >
          {{ $t('BRANCHES.VIEW_STAFF_SERVICES.DELETE') }}
        </button>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center text-slate-500 dark:text-slate-400">
      <p>{{ $t('BRANCHES.VIEW_STAFF_SERVICES.NO_SERVICES') }}</p>
    </div>
  </div>
</template>
